
import { computed, defineComponent, onMounted, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faKey, faPencil, faUser } from '@fortawesome/free-solid-svg-icons';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageContentSection from '@/layout/_shared/PageContentSection.vue';
import PageHeaderActions from '@/layout/header/PageHeaderActions.vue';
import PageHeaderActionItem from '@/layout/header/PageHeaderActionItem.vue';
import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
import DoiMatKhauModal from '@/views/crafted/pages/nguoi-dung/doi-mat-khau-modal/DoiMatKhauModal.vue';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import { useAccount } from '@/core/hooks/use-account';
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from '@/core/helpers/breadcrumb';
import { swalErrNotification } from '@/core/helpers/utils';
import { resolveAvatarUrl } from '@/core/helpers/avatar';
import { ProfileService } from '@/core/services/ProfileService';
import type { IMeProfile } from '@/core/interfaces/ApiResponses';
import type { User } from '@/store/modules/AuthModule';

export default defineComponent({
  name: 'chi-tiet-nguoi-dung',

  components: {
    DoiMatKhauModal,
    FontAwesomeIcon,
    LabelCustom,
    PageContentBase,
    PageContentSection,
    PageHeaderActionItem,
    PageHeaderActions,
  },

  setup() {
    const { push } = useRouterCustom();
    const { currentUserData } = useAccount();
    const isChangePasswordOpen = ref(false);
    const pageLoading = ref(false);
    const profile = ref<IMeProfile | null>(null);

    const user = computed<User>(() => currentUserData.value || ({} as User));

    const displayName = computed(
      () => profile.value?.ho_va_ten || user.value.fullName || '—'
    );

    const avatarUrl = computed(
      () =>
        resolveAvatarUrl(
          profile.value?.anh_dai_dien || user.value.avatar,
          profile.value?.anh_dai_dien_url
        )
    );

    const displayDepartment = computed(
      () =>
        profile.value?.don_vi?.ten_don_vi ||
        profile.value?.don_vi?.ma_don_vi ||
        user.value.departmentName ||
        '—'
    );

    const displayPosition = computed(
      () =>
        profile.value?.chuc_vu?.ten_chuc_vu ||
        user.value.positionName ||
        '—'
    );

    const displayPart = computed(
      () =>
        profile.value?.bo_phan?.ten_bo_phan ||
        user.value.partName ||
        '—'
    );

    const displayRole = computed(() => {
      if (profile.value?.role_names?.length) {
        return profile.value.role_names.filter(Boolean).join(', ');
      }
      const roles = user.value.roles || [];
      return roles.map((role) => role.name).filter(Boolean).join(', ');
    });

    const loadProfile = async () => {
      try {
        pageLoading.value = true;
        const {
          data: { data },
        } = await ProfileService.getProfile();
        profile.value = data;
        ProfileService.applyProfileToStore(data);
      } catch (error) {
        await swalErrNotification(
          error,
          'Không thể tải hồ sơ, vui lòng thử lại'
        );
      } finally {
        pageLoading.value = false;
      }
    };

    onMounted(async () => {
      setCurrentPageTitle('Hồ sơ của tôi');
      setBreadcrumbLinks([
        {
          text: 'Tài khoản của tôi',
        },
      ]);
      await loadProfile();
    });

    return {
      avatarUrl,
      displayDepartment,
      displayName,
      displayPart,
      displayPosition,
      displayRole,
      faKey,
      faPencil,
      faUser,
      isChangePasswordOpen,
      pageLoading,
      profile,
      push,
      user,
    };
  },
});
